import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import ReportTemperatureHistoryDateList from './ReportTemperatureHistoryDateList';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../provider/authProvider.js";
import axios from "axios";
import {
    outlinedActionButtonSx,
    confirmDialogActionsSx,
    confirmDialogButtonSx,
    confirmDialogBackdropSx,
    confirmDialogPaperSx,
    confirmDialogTitleSx,
} from "./windowChrome/index.js";


// Theme will be created inside the component to be reactive

// modalStyle will be created inside component to be theme-aware

const ReportWindow = ({
    variant = 'page',
    lockedSiteGuid = null,
    hideFooterActions = false,
    onActionsChange = null,
} = {}) => {
    const isModal = variant === 'modal';
    const navigate = useNavigate();
    const { clearToken } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingReport, setLoadingReport] = useState(false);
    const [daysToCompare, setDaysToCompare] = useState(5);
    const [plus, setPlus] = useState(() => global.gw.selectedSite?.temperatureComparison?.plus ?? 3);
    const [minus, setMinus] = useState(() => global.gw.selectedSite?.temperatureComparison?.minus ?? 3);
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 60);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [dateTo, setDateTo] = useState(() => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d;
    });
    const [typingTimeout, setTypingTimeout] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes
    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    // Also listen for the global theme change event
    useEffect(() => {
        if (global.gw.themeChanged) {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        }
    }, [global.gw.themeChanged]);

    // Poll for theme changes (fallback)
    useEffect(() => {
        const interval = setInterval(() => {
            const currentThemeValue = global.gw.theme || 'dark';
            if (currentThemeValue !== currentTheme) {
                setCurrentTheme(currentThemeValue);
                setForceUpdate(prev => prev + 1);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [currentTheme]);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        if (isModal) {
            // Match ReportTemperatureComparisonWindow (main Comparison form).
            return MuiMaterials.createTheme({
                components: {
                    MuiInputLabel: {
                        styleOverrides: {
                            root: {
                                color: isLight ? '#666666' : '#a8abb1',
                                fontSize: '14px',
                                '&.Mui-focused': {
                                    color: isLight ? '#333333' : '#f4cc8e',
                                },
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                fontSize: '14px',
                                textTransform: 'none',
                                background: isLight ? '#e0e0e0' : '#2d2d2d',
                                color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                            },
                        },
                    },
                    MuiIconButton: {
                        styleOverrides: {
                            sizeMedium: {
                                color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                color: isLight ? '#333333' : '#f4cc8e',
                                backgroundColor: isLight ? '#ffffff' : '#2d2d2d',
                                fontSize: '14px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: `1px solid ${isLight ? '#cccccc' : 'rgba(244,204,142,0.5)'}`,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: `1px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: `1px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                                },
                            },
                            input: {
                                fontSize: '14px',
                                padding: '8.5px 14px',
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '.MuiInputLabel-root.Mui-focused': {
                                    color: isLight ? '#333333' : '#f4cc8e',
                                },
                            },
                        },
                    },
                },
            });
        }
        return MuiMaterials.createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                            color: isLight ? '#000000' : '#f4cc8e',
                    fontSize: "18px",
                            ...(isLight && {
                                '&:not(.MuiInputLabel-shrink)': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '14px',
                                },
                            }),
                        },
                        shrink: {
                            top: isLight ? '8px' : undefined,
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                            background: isLight ? '#e0e0e0' : '#2d2d2d',
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                sizeMedium: {
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                            color: isLight ? '#333333' : '#f4cc8e',
                    "& .MuiOutlinedInput-notchedOutline": {
                                border: `0.5px solid ${isLight ? '#cccccc' : '#f4cc8e4d'}`,
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                                    border: `0.5px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                        },
                    },
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                                    border: `0.5px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                        },
                    },
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    ".MuiInputLabel-root.Mui-focused": {
                                color: isLight ? '#000000' : '#f4cc8e',
                    },
                },
            }
        },
    },
});
    }, [currentTheme, forceUpdate, isModal]);

    const fieldSx = useMemo(() => ({
        '& .MuiOutlinedInput-root': {
            color: getThemeColor('#333333', '#f4cc8e'),
            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
            fontSize: '14px',
            '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
            '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
        },
        '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1'), fontSize: '14px' },
        '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
        '& .MuiInputBase-input': { color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px' },
        '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
    }), [getThemeColor, currentTheme, forceUpdate]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleViewReportClick = () => {
        setLoadingReport(true);

        // Open blank window immediately (during user gesture) to avoid popup blockers
        const reportWindow = window.open('', '_blank');

        let calendarTimes = window.document.getElementsByClassName("calendarTime");
        let dates = [];
        const selectedBg = global.gw.theme === 'light' ? '#cccccc' : 'rgb(77, 77, 77)';
        Array.from(calendarTimes).forEach((c) => {
            const dateVal = c.getAttribute?.('data-date') || c.getAttribute?.('date') || c.attributes?.date?.value;
            const isSelected = c.style.background === selectedBg || c.style.backgroundColor === selectedBg;
            if (dateVal && isSelected) {
                dates.push(dateVal);
            }
        });

        const distinctDates = dates.length > 0 ? new Set(dates.map(d => (d || '').split('T')[0] || d)).size : 0;
        const daysForReport = distinctDates > 0 ? distinctDates : daysToCompare;

        const htask = () => {
            return createApiEndpoint().fetchTemperatureHistoryReport("compressed",
                dates.length > 0 ? dates : null,
                daysForReport,
                global.gw.selectedSite.temperatureComparison.plus,
                global.gw.selectedSite.temperatureComparison.minus,
                null);
        };
        htask().then((response) => {
            // Check if response is false (which might indicate a 401)
            if (response === false && global.gw.token && global.gw.auth) {
                // Server likely logged us out - throw 401 to trigger interceptor
                const authError = new Error('401 Unauthorized - Server logged out user');
                authError.response = { status: 401 };
                throw authError;
            }
            
            if (response) {
                var base64Bytes = global.base64StringToArrayBuffer(response.data.fetchTemperatureHistoryReport.value);
                const blob = new Blob([base64Bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                if (reportWindow) {
                    reportWindow.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            }
            else {

            }
            setLoadingReport(false);
        }).catch(function (error) {
            // If it's a 401 error, manually trigger the redirect (interceptor only catches axios errors)
            if (error.response && error.response.status === 401) {
                // console.log('ReportWindow: 401 error detected, clearing auth and redirecting to login');
                // Clear authentication state
                clearToken();
                delete axios.defaults.headers.common["Authorization"];
                global.gw.token = null;
                global.gw.auth = null;
                global.gw.isAuthenticated = false;
                // Redirect to login page
                setTimeout(() => {
                    window.location.href = "/emailLogin";
                }, 100);
                return;
            }
            // Also check for error message containing "401"
            if (error.message && (error.message.includes("401") || error.message.includes("Unauthorized"))) {
                // console.log('ReportWindow: Unauthorized error detected, clearing auth and redirecting to login');
                // Clear authentication state
                clearToken();
                delete axios.defaults.headers.common["Authorization"];
                global.gw.token = null;
                global.gw.auth = null;
                global.gw.isAuthenticated = false;
                // Redirect to login page
                setTimeout(() => {
                    window.location.href = "/emailLogin";
                }, 100);
                return;
            }
            
            if (error.message &&
                error.message.endsWith("401") &&
                global.gw.token &&
                global.gw.auth) {
                createApiEndpoint(global.Endpoints.GrainWave).fetchRenew();
            }
            if (reportWindow) reportWindow.close();
            setLoadingReport(false);
        });
    };

    const handleEmailReportClick = () => {
        setLoadingReport(true);

        let calendarTimes = window.document.getElementsByClassName("calendarTime");
        let dates = [];
        const selectedBg = global.gw.theme === 'light' ? '#cccccc' : 'rgb(77, 77, 77)';
        Array.from(calendarTimes).forEach((c) => {
            const dateVal = c.getAttribute?.('data-date') || c.getAttribute?.('date') || c.attributes?.date?.value;
            const isSelected = c.style.background === selectedBg || c.style.backgroundColor === selectedBg;
            if (dateVal && isSelected) {
                dates.push(dateVal);
            }
        });

        const distinctDates = dates.length > 0 ? new Set(dates.map(d => (d || '').split('T')[0] || d)).size : 0;
        const daysForReport = distinctDates > 0 ? distinctDates : daysToCompare;

        const htask = () => {
            return createApiEndpoint().fetchEmailTemperatureHistoryReport("compressed",
                dates.length > 0 ? dates : null,
                daysForReport,
                global.gw.selectedSite.temperatureComparison.plus,
                global.gw.selectedSite.temperatureComparison.minus,
                null);
        };
        htask().then((response) => {
            // Check if response is false (which might indicate a 401)
            if (response === false && global.gw.token && global.gw.auth) {
                // Server likely logged us out - throw 401 to trigger interceptor
                const authError = new Error('401 Unauthorized - Server logged out user');
                authError.response = { status: 401 };
                throw authError;
            }
            
            setLoadingReport(false);
        }).catch(function (error) {
            // If it's a 401 error, manually trigger the redirect (interceptor only catches axios errors)
            if (error.response && error.response.status === 401) {
                // console.log('ReportWindow: 401 error detected, clearing auth and redirecting to login');
                // Clear authentication state
                clearToken();
                delete axios.defaults.headers.common["Authorization"];
                global.gw.token = null;
                global.gw.auth = null;
                global.gw.isAuthenticated = false;
                // Redirect to login page
                setTimeout(() => {
                    window.location.href = "/emailLogin";
                }, 100);
                return;
            }
            // Also check for error message containing "401"
            if (error.message && (error.message.includes("401") || error.message.includes("Unauthorized"))) {
                // console.log('ReportWindow: Unauthorized error detected, clearing auth and redirecting to login');
                // Clear authentication state
                clearToken();
                delete axios.defaults.headers.common["Authorization"];
                global.gw.token = null;
                global.gw.auth = null;
                global.gw.isAuthenticated = false;
                // Redirect to login page
                setTimeout(() => {
                    window.location.href = "/emailLogin";
                }, 100);
                return;
            }
            
            if (error.message &&
                error.message.endsWith("401") &&
                global.gw.token &&
                global.gw.auth) {
                createApiEndpoint(global.Endpoints.GrainWave).fetchRenew();
            }
            else {

            }
            setLoadingReport(false);
        });
    };

    const actionsRef = useRef({ onView: () => {}, onEmail: () => {} });
    actionsRef.current = {
        onView: handleViewReportClick,
        onEmail: () => setOpen(true),
    };
    useEffect(() => {
        if (!onActionsChange) return undefined;
        onActionsChange({
            onView: () => actionsRef.current.onView?.(),
            onEmail: () => actionsRef.current.onEmail?.(),
            loadingReport,
            loadingEmail: loadingReport,
        });
        return () => onActionsChange(null);
    }, [onActionsChange, loadingReport]);

    useEffect(() => {
        if (!isModal) return;
        setIsLoading(true);
    }, [isModal, lockedSiteGuid]);

    useEffect(() => {
        (async () => {
            // Wait for token and auth to be available
            const initializeWhenReady = () => {
                if (global.gw.token && global.gw.auth) {
                    return true;
                }
                return false;
            };

            // Try immediately
            if (!initializeWhenReady()) {
                // If token isn't available yet, wait a bit and try again
                let checkCount = 0;
                const maxChecks = 50; // 5 seconds total (50 * 100ms)
                const checkToken = setInterval(() => {
                    checkCount++;
                    if (initializeWhenReady()) {
                        clearInterval(checkToken);
                        initializeSite();
                    } else if (checkCount >= maxChecks) {
                        console.warn('ReportWindow: Token not available after waiting, attempting initialization anyway...');
                        clearInterval(checkToken);
                        initializeSite();
                    }
                }, 100);
                return () => clearInterval(checkToken);
            } else {
                initializeSite();
            }

            async function initializeSite() {
                try {
                    // console.log('ReportWindow: Starting initialization...');
                    // console.log('ReportWindow: selectedSite.siteGuid:', global.gw.selectedSite?.siteGuid || 'missing');

                    if (lockedSiteGuid) {
                        const want = global.normalizeGuidForCompare
                            ? global.normalizeGuidForCompare(lockedSiteGuid)
                            : String(lockedSiteGuid || '').toLowerCase();
                        const info = (global.gw.siteInfos || []).find((s) => {
                            const g = s?.siteGuid;
                            if (!g) return false;
                            const have = global.normalizeGuidForCompare
                                ? global.normalizeGuidForCompare(g)
                                : String(g).toLowerCase();
                            return have === want;
                        });
                        if (info) {
                            if (!global.gw.selectedSite || global.gw.selectedSite.siteGuid !== info.siteGuid) {
                                if (global.gw.selectedSite?.refreshId) {
                                    clearTimeout(global.gw.selectedSite.refreshId);
                                }
                                global.gw.selectedSite = new global.Site();
                                global.gw.selectedSite.siteIndex = info.siteIndex;
                                global.gw.selectedSite.siteGuid = info.siteGuid;
                                global.gw.selectedSite.siteName = info.siteName;
                                global.gw.selectedSite.lastScan = info.lastScan;
                            } else {
                                global.gw.selectedSite.siteGuid = info.siteGuid;
                                global.gw.selectedSite.siteName = info.siteName;
                            }
                        } else if (global.gw.selectedSite) {
                            global.gw.selectedSite.siteGuid = lockedSiteGuid;
                        }
                    }
                    
                    // If siteGuid is not set, try to restore it from localStorage
                    if (!global.gw.selectedSite.siteGuid) {
                        const savedSite = global.restoreSelectedSite();
                        if (savedSite && savedSite.siteGuid) {
                            // console.log('ReportWindow: Restored selected site from localStorage:', savedSite.siteGuid);
                            global.gw.selectedSite.siteIndex = savedSite.siteIndex;
                            global.gw.selectedSite.siteGuid = savedSite.siteGuid;
                            global.gw.selectedSite.siteName = savedSite.siteName;
                            if (savedSite.lastScan) {
                                global.gw.selectedSite.lastScan = savedSite.lastScan;
                            }
                        }
                    }
                    
                    // console.log('ReportWindow: Starting initializeCustomerSite...');
                    await global.initializeCustomerSite(navigate);
                    // console.log('ReportWindow: initializeCustomerSite completed');
                    // console.log('ReportWindow: selectedSite.isSiteInitialized:', global.gw.selectedSite.isSiteInitialized);
                    
                    // Wait a bit for site to be fully initialized
                    let retryCount = 0;
                    const maxRetries = 30; // 3 seconds total
                    while (retryCount < maxRetries) {
                        if (global.gw.selectedSite.isSiteInitialized) {
                            // Site is initialized, we're ready
                            // console.log('ReportWindow: Site initialized, ready to display');
                            setIsLoading(false);
                            return;
                        }
                        
                        // Wait 100ms before checking again
                        await new Promise(resolve => setTimeout(resolve, 100));
                        retryCount++;
                        
                        if (retryCount % 10 === 0) {
                            // console.log(`ReportWindow: Still waiting for site initialization... (attempt ${retryCount}/${maxRetries})`);
                        }
                    }
                    
                    // If we get here, site didn't initialize in time - but still show the UI
                    console.warn('ReportWindow: Site initialization timeout, showing UI anyway');
                    setIsLoading(false);
                } catch (error) {
                    console.error('ReportWindow: Error initializing site:', error);
                    console.error('ReportWindow: Error stack:', error.stack);
                    
                    // Check for 401 errors
                    const is401Error = (error.response && error.response.status === 401) ||
                                      (error.message && (error.message.includes("401") || error.message.includes("Unauthorized")));
                    
                    if (is401Error && global.gw.token && global.gw.auth) {
                        // console.log('ReportWindow: 401 error detected during initialization, redirecting to login');
                        clearToken();
                        delete axios.defaults.headers.common["Authorization"];
                        global.gw.token = null;
                        global.gw.auth = null;
                        global.gw.isAuthenticated = false;
                        setTimeout(() => {
                            window.location.href = "/emailLogin";
                        }, 100);
                        return;
                    }
                    
                    setIsLoading(false);
                }
            }
        })();
    }, [lockedSiteGuid, navigate]);

    if (isLoading) {
        return (
            <MuiMaterials.Box
                key={`report-spinner-${currentTheme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#333333', 'rgb(244, 204, 142)'),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    ...(isModal
                        ? {
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            background: "transparent",
                            pointerEvents: "none",
                        }
                        : {
                            position: "fixed",
                            top: "0",
                            left: "0",
                            right: "0",
                            bottom: "0",
                            width: "100%",
                            height: "100%",
                            zIndex: 9999,
                            background: getThemeColor('#ffffff', 'transparent'),
                        }),
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        lineHeight: 0,
                        margin: 0,
                        padding: 0,
                        pointerEvents: "auto",
                    }}
                >
                    <MuiMaterials.RotatingLines
                        strokeColor={getThemeColor('#333333', '#f4cc8e')}
                        strokeWidth="3"
                        animationDuration="0.72"
                        width="48"
                        visible={true}
                    />
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        );
    }

    return (
        <div>
            <MuiMaterials.Box
                key={`report-window-${currentTheme}-${forceUpdate}`}
                className="reportWindow hide-scrollbar"
                sx={{
                    position: isModal ? "relative" : "fixed",
                    top: isModal ? undefined : 0,
                    zIndex: isModal ? undefined : "1000",
                    width: "100%",
                    flexDirection: "column",
                    alignItems: "stretch",
                    overflowX: isModal ? "visible" : "hidden",
                    paddingBottom: "4px",
                    height: isModal ? "auto" : "-webkit-fill-available",
                    borderStyle: "solid",
                    borderColor: getThemeColor('#cccccc', '#121212'),
                    borderWidth: isModal ? 0 : "0.5px",
                    borderRadius: "4px",
                    cursor: "default",
                    background: getThemeColor('#ffffff', '#2d2d2d'),
                    // Modal: let ModalContentTile scroll — overflow:auto here traps the wheel.
                    overflowY: isModal ? "visible" : "auto",
                    ...(isModal ? {} : { height: "100%" }),
                }}
            >
                {!isModal ? (
                <MuiMaterials.Box
                    sx={{
                        background: getThemeColor('#e0e0e0', '#121212'),
                        height: "44px",
                        width: "100%",
                        display: "inline-block",
                    }}
                >
                    <MuiMaterials.Box
                        sx={{
                            //flex: 1,
                            background: getThemeColor('#e0e0e0', '#121212'),
                            color: getThemeColor('#333333', '#f4cc8e'),
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: "2px 5px 1px 0px",
                            alignItems: "center",
                        }}
                    >
                        <div
                            className="reportWindowTitle"
                            style={{
                                position: "absolute",
                                left: "5px",
                                top: "3px",
                                //flex: 1,
                                padding: "10px 2px",
                                //display: "flex",
                                alignItems: "center",
                                fontWeight: "700",
                                color: getThemeColor('#333333', '#f4cc8e'),
                            }}
                        >
                            {global.gw.selectedSite.siteName}
                        </div>
                        <MuiMaterials.Button
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                //margin: "auto",
                                maxWidth: "40px",
                                maxHeight: "40px",
                                minWidth: "40px",
                                minHeight: "40px",
                                marginRight: "-6px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                            }}
                            onClick={e => {
                                let reportWindow = window.document.getElementsByClassName("reportWindow")[0];
                                if (reportWindow) {
                                    reportWindow.style.display = "none";
                                }
                                navigate("/sites");
                            }}
                        >
                            <CloseIcon className="closeCarbonDioxide" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
                ) : null}
                <MuiMaterials.Box
                    className="reportTable hide-scrollbar"
                    sx={{
                        margin: isModal ? "0" : "14px 2px",
                        overflowX: isModal ? "visible" : "auto",
                        overflowY: isModal ? "visible" : "auto",
                        textAlign: "center",
                        top: isModal ? undefined : 80,
                        width: "100%",
                        maxHeight: isModal ? "none" : "calc(100vh - 60px)",
                        ...(isModal
                            ? {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                // Outlined InputLabels sit above the field border; keep them inside the tile.
                                paddingTop: "12px",
                                boxSizing: "border-box",
                            }
                            : {}),
                    }}
                >
                    {!(isModal && hideFooterActions) ? (
                    <MuiMaterials.Box
                        sx={{
                            padding: "0px 0px 5px 5px",
                            color: getThemeColor('#333333', '#f4cc8e'),
                            position: "sticky",
                            top: 0,
                            left: 0,
                            right: 0,
                            width: "100%",
                            textAlign: "left",
                            display: "flex",
                            alignContent: "center",
                            justifyContent: "space-between",
                            alignItems: "center",
                            zIndex: 1100,
                            background: getThemeColor('#ffffff', '#2d2d2d'),
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            marginBottom: "4px",
                            flexShrink: 0,
                        }}
                    >
                        Comparison
                        <MuiMaterials.Box>
                            <MuiMaterials.LoadingButton
                                key={`report-view-button-${currentTheme}-${forceUpdate}`}
                                type="button"
                                loading={loadingReport}
                                variant="outlined"
                                size="small"
                                className="reportButton"
                                sx={outlinedActionButtonSx()}
                                onClick={handleViewReportClick}
                            >
                                View
                            </MuiMaterials.LoadingButton>
                            <MuiMaterials.LoadingButton
                                key={`report-email-button-${currentTheme}-${forceUpdate}`}
                                type="button"
                                loading={loadingReport}
                                variant="outlined"
                                size="small"
                                className="emailReportButton"
                                sx={{ ...outlinedActionButtonSx(), ml: 0.5 }}
                                onClick={e => {
                                    setOpen(true);
                                    e.preventDefault = true;
                                }}
                            >
                                Email
                            </MuiMaterials.LoadingButton>
                        </MuiMaterials.Box>
                    </MuiMaterials.Box>
                    ) : null}
                    <MuiMaterials.Modal
                        key={`report-modal-${currentTheme}-${forceUpdate}`}
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                        sx={confirmDialogBackdropSx()}
                    >
                        <MuiMaterials.Box sx={confirmDialogPaperSx()}>
                            <MuiMaterials.Typography
                                id="modal-modal-title"
                                variant="h6"
                                component="h2"
                                sx={confirmDialogTitleSx()}
                            >
                                {"Email this report?"}
                            </MuiMaterials.Typography>
                            <div style={confirmDialogActionsSx()}>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={confirmDialogButtonSx()}
                                    onClick={e => {
                                        handleClose();
                                        handleEmailReportClick();
                                        //e.preventDefault = true;
                                    }}
                                >
                                    Yes
                                </MuiMaterials.Button>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    sx={confirmDialogButtonSx()}
                                    onClick={handleClose}
                                >
                                    No
                                </MuiMaterials.Button>
                            </div>
                        </MuiMaterials.Box>
                    </MuiMaterials.Modal>
                    <MuiMaterials.Box
                        sx={{
                            marginTop: isModal ? 0 : "4px",
                            marginBottom: isModal ? 1.5 : 0,
                            width: isModal ? "100%" : undefined,
                            display: isModal ? "flex" : undefined,
                            justifyContent: isModal ? "center" : undefined,
                            // Extra room so Date From / Days / Plus / Minus labels aren't clipped.
                            ...(isModal
                                ? {
                                    paddingTop: "4px",
                                    overflow: "visible",
                                }
                                : {}),
                        }}
                    >
                        {isModal ? (
                            <MuiMaterials.ThemeProvider theme={theme} key={`report-theme-modal-${currentTheme}-${forceUpdate}`}>
                                <MuiMaterials.Box
                                    sx={{
                                        display: 'grid',
                                        gap: 1.5,
                                        alignItems: 'start',
                                        justifyContent: 'center',
                                        margin: '0 auto',
                                        width: 'max-content',
                                        maxWidth: '100%',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, minmax(100px, 140px))',
                                            sm: 'repeat(6, minmax(88px, 140px))',
                                        },
                                    }}
                                >
                                    <MuiMaterials.LocalizationProvider dateAdapter={MuiMaterials.AdapterDateFns}>
                                        <MuiMaterials.DatePicker
                                            label="Date From"
                                            value={dateFrom}
                                            onChange={(newValue) => {
                                                if (newValue) {
                                                    const d = new Date(newValue);
                                                    d.setHours(0, 0, 0, 0);
                                                    setDateFrom(d);
                                                    const to = new Date();
                                                    to.setHours(23, 59, 59, 999);
                                                    if (to < d) setDateTo(d);
                                                    else setDateTo(to);
                                                }
                                            }}
                                            slotProps={{
                                                textField: { size: 'small' },
                                                popper: {
                                                    sx: {
                                                        '.MuiDayCalendar-weekDayLabel': { color: getThemeColor('#333333', '#f4cc8e') },
                                                        '.MuiPaper-root': {
                                                            border: `1px solid ${getThemeColor('#cccccc', 'rgba(244,204,142,0.5)')}`,
                                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                                            color: getThemeColor('#333333', '#f4cc8e'),
                                                        },
                                                        '.MuiButtonBase-root': { color: getThemeColor('#333333', '#f4cc8e') },
                                                        '.MuiPickersDay-root.Mui-selected': {
                                                            background: getThemeColor('#e0e0e0', 'black'),
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={{ ...fieldSx, gridColumn: 'span 2', minWidth: 0, width: '100%' }}
                                        />
                                    </MuiMaterials.LocalizationProvider>
                                    <MuiMaterials.TextField
                                        className="reportDaysToCompare"
                                        label="Days to compare"
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        value={daysToCompare}
                                        onFocus={(e) => e.target.select?.()}
                                        onChange={(e) => {
                                            setDaysToCompare(Math.max(0, Math.min(365, +e.target.value || 0)));
                                        }}
                                        sx={{ ...fieldSx, gridColumn: 'span 2', minWidth: 0, width: '100%' }}
                                    />
                                    <MuiMaterials.TextField
                                        className="reportPlus"
                                        label="Plus"
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        value={plus}
                                        onFocus={(e) => e.target.select?.()}
                                        onChange={(e) => {
                                            const n = +e.target.value || 0;
                                            setPlus(n);
                                            if (global.gw.selectedSite?.temperatureComparison) {
                                                global.gw.selectedSite.temperatureComparison.plus = n;
                                            }
                                        }}
                                        sx={{ ...fieldSx, gridColumn: 'span 1', minWidth: 0, width: '100%' }}
                                    />
                                    <MuiMaterials.TextField
                                        className="reportMinus"
                                        label="Minus"
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        value={minus}
                                        onFocus={(e) => e.target.select?.()}
                                        onChange={(e) => {
                                            const n = +e.target.value || 0;
                                            setMinus(n);
                                            if (global.gw.selectedSite?.temperatureComparison) {
                                                global.gw.selectedSite.temperatureComparison.minus = n;
                                            }
                                        }}
                                        sx={{ ...fieldSx, gridColumn: 'span 1', minWidth: 0, width: '100%' }}
                                    />
                                </MuiMaterials.Box>
                            </MuiMaterials.ThemeProvider>
                        ) : (
                            <>
                        <MuiMaterials.Box sx={{ width: "100%", marginBottom: "8px" }}>
                            <MuiMaterials.ThemeProvider theme={theme} key={`report-theme-${currentTheme}-${forceUpdate}`}>
                                <MuiMaterials.LocalizationProvider dateAdapter={MuiMaterials.AdapterDateFns}>
                                    <MuiMaterials.DatePicker
                                        label="Date From"
                                        value={dateFrom}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                const d = new Date(newValue);
                                                d.setHours(0, 0, 0, 0);
                                                setDateFrom(d);
                                                const to = new Date();
                                                to.setHours(23, 59, 59, 999);
                                                if (to < d) setDateTo(d);
                                                else setDateTo(to);
                                            }
                                        }}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    ".MuiDayCalendar-weekDayLabel": { color: getThemeColor("#333333", "#f4cc8e") },
                                                    ".MuiPaper-root": {
                                                        border: `1px solid ${getThemeColor("#cccccc", "#f4cc8e")}`,
                                                        background: getThemeColor("#ffffff", "#2d2d2d"),
                                                        color: getThemeColor("#333333", "#f4cc8e"),
                                                    },
                                                    ".MuiButtonBase-root": { color: getThemeColor("#333333", "#f4cc8e") },
                                                    ".MuiPickersDay-root.Mui-selected": {
                                                        background: getThemeColor("#e0e0e0", "black"),
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            marginTop: "10px",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: getThemeColor("#cccccc", "#f4cc8e") },
                                                "&:hover fieldset": { borderColor: getThemeColor("#666666", "#f4cc8e") },
                                                "&.Mui-focused fieldset": { borderColor: getThemeColor("#666666", "#f4cc8e") },
                                            },
                                            "& .MuiInputLabel-root": { color: getThemeColor("#000000", "#f4cc8e") },
                                            "& .MuiInputBase-input": { color: getThemeColor("#333333", "#f4cc8e") },
                                        }}
                                    />
                                </MuiMaterials.LocalizationProvider>
                            </MuiMaterials.ThemeProvider>
                        </MuiMaterials.Box>
                        <MuiMaterials.Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", marginTop: "10px", marginBottom: "15px", gap: "8px" }}>
                            <MuiMaterials.ThemeProvider theme={theme} key={`report-theme-2-${currentTheme}-${forceUpdate}`}>
                                <MuiMaterials.TextField
                                    key={`report-days-${currentTheme}-${forceUpdate}`}
                                    className="reportDaysToCompare"
                                    label="Days"
                                    autoComplete="off"
                                    sx={{
                                        transform: "scale(0.95)",
                                        transformOrigin: "left",
                                        width: "72px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    inputProps={{
                                        style: {
                                            padding: "14px 5px",
                                            textAlign: "center",
                                        }
                                    }}
                                    value={daysToCompare}
                                    onFocus={() => {
                                        let reportDaysToCompare = window.document.querySelector('.reportDaysToCompare .MuiInputBase-input');
                                        if (reportDaysToCompare) {
                                            reportDaysToCompare.select();
                                        }
                                    }}
                                    onChange={(e) => {
                                        setDaysToCompare(Math.max(0, Math.min(365, +e.target.value || 0)));
                                    }}
                                    type="number"
                                />
                                <MuiMaterials.TextField
                                    key={`report-plus-${currentTheme}-${forceUpdate}`}
                                    className="reportPlus"
                                    label="Plus"
                                    autoComplete="off"
                                    sx={{
                                        transform: "scale(0.95)",
                                        transformOrigin: "left",
                                        width: "72px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    inputProps={{
                                        style: {
                                            padding: "14px 5px",
                                            textAlign: "center",
                                        }
                                    }}
                                    value={plus}
                                    onFocus={() => {
                                        let reportPlus = window.document.querySelector('.reportPlus .MuiInputBase-input');
                                        if (reportPlus) {
                                            reportPlus.select();
                                        }
                                    }}
                                    onChange={(e) => {
                                        const n = +e.target.value || 0;
                                        setPlus(n);
                                        if (global.gw.selectedSite?.temperatureComparison) {
                                            global.gw.selectedSite.temperatureComparison.plus = n;
                                        }
                                    }}
                                    type="number"
                                />
                                <MuiMaterials.TextField
                                    key={`report-minus-${currentTheme}-${forceUpdate}`}
                                    className="reportMinus"
                                    label="Minus"
                                    autoComplete="off"
                                    sx={{
                                        transform: "scale(0.95)",
                                        transformOrigin: "left",
                                        width: "72px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    inputProps={{
                                        style: {
                                            padding: "14px 5px",
                                            textAlign: "center",
                                        }
                                    }}
                                    value={minus}
                                    onFocus={() => {
                                        let reportMinus = window.document.querySelector('.reportMinus .MuiInputBase-input');
                                        if (reportMinus) {
                                            reportMinus.select();
                                        }
                                    }}
                                    onChange={(e) => {
                                        const n = +e.target.value || 0;
                                        setMinus(n);
                                        if (global.gw.selectedSite?.temperatureComparison) {
                                            global.gw.selectedSite.temperatureComparison.minus = n;
                                        }
                                    }}
                                    type="number"
                                /> 
                            </MuiMaterials.ThemeProvider>
                        </MuiMaterials.Box>
                            </>
                        )}
                    </MuiMaterials.Box>
                    <ReportTemperatureHistoryDateList
                        daysToCompare={daysToCompare}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onSelectedDatesChange={setDaysToCompare}
                    />
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        </div>
    );
}
export default ReportWindow;